import { makeStyles } from '@material-ui/styles';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useCallback } from 'react';
import BackBtn from './back-btn';
import FinishSignupForEmailOrPhone from './finish-signup-for-email-or-phone';
import LoginForSetEmail from './login-for-email';
import LoginForSetEmailPassword from './login-for-email/password';
import LoginForSetPhoneNumber from './login-for-phone-number';
import LoginForSetPhoneNumberCode from './login-for-phone-number/code';
import SignupForEmail from './login-for-email/signup';

const { Title } = Typography;

export const loginContentTypes = {
  SET_EMAIL: 'SET_EMAIL',
  SET_EMAIL_PASSWORD: 'SET_EMAIL_PASSWORD',
  EMAIL_FOR_SIGNUP: 'EMAIL_FOR_SIGNUP',
  FINISH_EMAIL_FOR_SIGNUP: 'FINISH_EMAIL_FOR_SIGNUP',
  SET_PHONE_NUMBER: 'SET_PHONE_NUMBER',
  SET_PHONE_NUMBER_CODE: 'SET_PHONE_NUMBER_CODE',
  FINISH_PHONE_NUMBER_FOR_SIGNUP: 'FINISH_PHONE_NUMBER_FOR_SIGNUP',
};

const useStyles = makeStyles(
  () => ({
    wrapper: {
      width: 480,

      '& > *:not(:first-child)': {
        width: '100%',
        marginTop: 20,
      },
    },
    title: {
      lineHeight: '38px !important',
      margin: '0 !important',
      opacity: 0.85,
    },
    backBtn: {
      fontWeight: 600,
      '&:hover': {
        fontWeight: 600,
        backgroundColor: '#f5f5f5 !important',
        color: 'rgba(0, 0, 0, 0.65)',
      },
    },
  }),
  { name: 'LoginOrSignUp' },
);

const LoginOrSignUpForPhoneNumber = ({
  loginContentType,
  handleNextContent,
  setLoginContentType,
  clearLoginValue,
  loginValue,
  setSignupValue,
}) => {
  const [codeType, setCodeType] = useState();

  if (loginContentType === loginContentTypes.SET_PHONE_NUMBER) {
    return (
      <LoginForSetPhoneNumber
        handleNextContent={handleNextContent}
        onChangeLoginType={setLoginContentType}
        clearLoginValue={clearLoginValue}
        codeType={codeType}
        setCodeType={setCodeType}
      />
    );
  }

  if (loginContentType === loginContentTypes.SET_PHONE_NUMBER_CODE) {
    return (
      <LoginForSetPhoneNumberCode
        codeType={codeType}
        handleNextContent={handleNextContent}
        loginPhone={loginValue}
        setSignupValue={setSignupValue}
      />
    );
  }
  return null;
};

LoginOrSignUpForPhoneNumber.propTypes = {
  loginContentType: PropTypes.string,
  handleNextContent: PropTypes.func,
  setLoginContentType: PropTypes.func,
  clearLoginValue: PropTypes.func,
  loginValue: PropTypes.string,
  setSignupValue: PropTypes.func,
};

const LoginContent = props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [loginContentType, setLoginContentType] = useState(loginContentTypes.SET_EMAIL);
  const [showWarning, setShowWarning] = useState(false);
  const [loginValue, setLoginValue] = useState();
  const [signupValue, setSignupValue] = useState();

  const handleNextContent = (inputValue, contentType, needShowWarning = false) => {
    setLoginValue(inputValue);
    setLoginContentType(contentType);
    setShowWarning(needShowWarning);
  };

  const clearLoginValue = () => {
    setLoginValue();
  };

  const loginContentTitle = useMemo(() => {
    switch (loginContentType) {
      case loginContentTypes.SET_EMAIL_PASSWORD:
        return t('log_in');
      case loginContentTypes.SET_PHONE_NUMBER:
      case loginContentTypes.SET_EMAIL:
        return t('log_in_or_sign_up');
      case loginContentTypes.EMAIL_FOR_SIGNUP:
        return t('sign_up');
      case loginContentTypes.EMAIL_RESET_PASSWORD:
        return t('reset_your_password');
      case loginContentTypes.SET_PHONE_NUMBER_CODE:
        return t('confirm_your_number');
      case loginContentTypes.FINISH_EMAIL_FOR_SIGNUP:
      case loginContentTypes.FINISH_PHONE_NUMBER_FOR_SIGNUP:
        return t('finish_signing_up');
      default:
        return;
    }
  }, [loginContentType, t]);

  const backLoginContentType = useMemo(() => {
    switch (loginContentType) {
      case loginContentTypes.SET_EMAIL_PASSWORD:
      case loginContentTypes.EMAIL_FOR_SIGNUP:
        return loginContentTypes.SET_EMAIL;

      case loginContentTypes.FINISH_EMAIL_FOR_SIGNUP:
        return loginContentTypes.EMAIL_FOR_SIGNUP;

      case loginContentTypes.SET_PHONE_NUMBER_CODE:
      case loginContentTypes.FINISH_PHONE_NUMBER_FOR_SIGNUP:
        return loginContentTypes.SET_PHONE_NUMBER;

      default:
        return;
    }
  }, [loginContentType]);

  const handleClickBackBtn = useCallback(() => {
    setLoginContentType(backLoginContentType);
  }, [backLoginContentType]);

  const backPhoneNumberLogin = () => {
    setLoginContentType(loginContentTypes.SET_PHONE_NUMBER_CODE);
  };

  return (
    <div className={classes.wrapper}>
      {backLoginContentType && <BackBtn className={classes.backBtn} onClick={handleClickBackBtn} />}
      {loginContentTitle && (
        <Title level={2} className={classes.title}>
          {loginContentTitle}
        </Title>
      )}

      {loginContentType === loginContentTypes.SET_EMAIL && (
        <LoginForSetEmail
          handleNextContent={handleNextContent}
          onChangeLoginType={setLoginContentType}
          clearLoginValue={clearLoginValue}
        />
      )}

      {loginContentType === loginContentTypes.SET_EMAIL_PASSWORD && (
        <LoginForSetEmailPassword
          loginEmail={loginValue}
          handleNextContent={handleNextContent}
          history={props?.history}
          showWarning={showWarning}
        />
      )}

      {loginContentType === loginContentTypes.EMAIL_FOR_SIGNUP && (
        <SignupForEmail
          loginEmail={loginValue}
          handleNextContent={handleNextContent}
          setSignupValue={setSignupValue}
          showWarning={showWarning}
        />
      )}

      <LoginOrSignUpForPhoneNumber
        loginContentType={loginContentType}
        handleNextContent={handleNextContent}
        setLoginContentType={setLoginContentType}
        clearLoginValue={clearLoginValue}
        loginValue={loginValue}
        setSignupValue={setSignupValue}
      />

      {(loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP ||
        loginContentType === loginContentTypes.FINISH_PHONE_NUMBER_FOR_SIGNUP) && (
        <FinishSignupForEmailOrPhone
          signupValue={signupValue}
          loginContentType={loginContentType}
          backPhoneNumberLogin={backPhoneNumberLogin}
        />
      )}
    </div>
  );
};

LoginContent.propTypes = {
  history: PropTypes.object,
};

export default LoginContent;
